<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import { nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { provideControlDefaults } from '@/components/base/_internal/control';
import { cn } from '@/utils/shadcn/lib/utils';
import { provideFormRow } from './internal/context';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = defineProps<{
    class?: HTMLAttributes['class']
}>();

provideFormRow(true);
provideControlDefaults({ legacyWidth: false });

const root = useTemplateRef<HTMLElement>('root');
const grid = useTemplateRef<HTMLElement>('grid');
const columns = ref<string>();
const stacked = ref(false);

// A field whose control fills takes a share of the free space, down to a small control's width;
// one with a narrower control, and anything that is not a field, keeps its own width. The fields
// say which through `data-control-width`, read in DOM order so a field toggled by v-if keeps its place.
function updateColumns(): void {
    const children = Array.from(grid.value?.children ?? []);

    columns.value = children
        .map((child) => (child.getAttribute('data-control-width') === 'full'
            ? 'minmax(min(var(--control-width-sm), 100%), 1fr)'
            // Not `auto`, which would squeeze a date picker below its format before the row stacks.
            : 'max-content'))
        .join(' ') || undefined;
}

// Whether the fields fit side by side depends on what they hold, not on a fixed breakpoint, so the
// row measures it: it stacks once the fields overflow it, and remembers the width they needed to
// come back side by side once there is room for them again.
let neededWidth = 0;

async function updateStacking(): Promise<void> {
    if (!root.value || !grid.value) {
        return;
    }

    if (!stacked.value) {
        if (grid.value.scrollWidth > grid.value.clientWidth) {
            neededWidth = grid.value.scrollWidth;
            stacked.value = true;
        }
        return;
    }

    if (root.value.clientWidth >= neededWidth) {
        stacked.value = false;
        // The fields may need more room than they did when they stacked.
        await nextTick();
        await updateStacking();
    }
}

async function onFieldsChanged(): Promise<void> {
    updateColumns();

    // What the fields need side by side can only be measured side by side.
    stacked.value = false;
    await nextTick();
    await updateStacking();
}

let childObserver: MutationObserver | null = null;
let widthObserver: MutationObserver | null = null;
let resizeObserver: ResizeObserver | null = null;

onMounted(async () => {
    // Two observers, so a change inside a field (an error appearing, a select's tags) is not one.
    childObserver = new MutationObserver(onFieldsChanged);
    childObserver.observe(grid.value!, { childList: true });
    widthObserver = new MutationObserver(onFieldsChanged);
    widthObserver.observe(grid.value!, { subtree: true, attributeFilter: ['data-control-width'] });

    resizeObserver = new ResizeObserver(() => updateStacking());
    resizeObserver.observe(root.value!);

    await onFieldsChanged();
});

onBeforeUnmount(() => {
    childObserver?.disconnect();
    widthObserver?.disconnect();
    resizeObserver?.disconnect();
});
</script>

<template>
    <div
        ref="root"
        data-slot="form-row"
        :data-stacked="stacked || undefined"
        :class="cn('sph-form-row', props.class)"
    >
        <div
            ref="grid"
            class="sph-form-row__grid"
            :style="columns ? { '--form-row-columns': columns } : undefined"
        >
            <slot></slot>
        </div>
    </div>
</template>

<style scoped>
.sph-form-row {
    min-width: 0;
}

.sph-form-row__grid {
    display: grid;
    grid-template-columns: var(--form-row-columns, none);
    grid-template-rows: repeat(5, auto);
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    /* Without a filling field the free space would stretch the fixed ones apart. */
    justify-content: start;
    column-gap: var(--field-gap-x);
}

/* Anything that is not a field, a button say, sits on the controls' line. */
:slotted(:not([data-slot='form-field'])) {
    grid-row: 3;
    align-self: center;
}

/* Stacked, every control takes the full width, as the fields' own do. */
.sph-form-row[data-stacked] > .sph-form-row__grid {
    --control-width-stretch: 100%;

    display: flex;
    flex-direction: column;
    gap: var(--field-gap-y);
}

.sph-form-row[data-stacked] :slotted(:not([data-slot='form-field'])) {
    align-self: flex-start;
}
</style>
